// ─── Carte « accès d'un élément » RÉUTILISABLE (Core) ─────────────
// Une ressource (un calendrier, un inventaire, un plan…) + sa liste d'accès.
// Modèle unifié `acces` (cf. niveaux.js). Le propriétaire est implicite (accès
// complet, non listé). On ajoute des membres OU « Tout l'espace », chacun avec
// un niveau. Rendu partout depuis Profil ; onChange(nextAcces) remonte l'objet.
//
//   <SectionAcces titre="Famille" emoji="📅"
//     acces={cal.acces} membres={[{uid,nom}]} ownerUid={…}
//     onChange={(nextAcces) => …} />
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import LigneSwipe from '../ui/LigneSwipe';
import FeuilleNiveauAcces from './FeuilleNiveauAcces';
import { useOuvrirFormAcces } from './FormAccesContext';
import { NIVEAUX, definirNiveau, rangNiveau } from './niveaux';
import { useNoms } from '../services/profilsPublics';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const CLE_NIVEAU = { lecteur: 'acces.niveauLecteur', contributeur: 'acces.niveauContributeur', gestionnaire: 'acces.niveauGestionnaire' };

export default function SectionAcces({ titre, emoji = '📄', sousTitre, acces, membres = [], ownerUid, niveaux = NIVEAUX, onChange }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [form, setForm] = useState(null);   // { mode, cible, cibleNom, niveauInit } → FeuilleNiveauAcces

  const a = acces || {};
  // On accorde souvent un accès à quelqu'un qui n'est PAS membre de l'espace
  // (invité en attente, personne d'un autre espace) : son nom n'est alors nulle
  // part en local. Le registre public le résout ; les membres restent
  // prioritaires, leur libellé étant celui choisi dans l'espace.
  const nomsPublics = useNoms(Object.keys(a).filter(k => k !== '*'));
  const nomDe = (uid) => nomsPublics(uid, membres.find(m => m.uid === uid)?.nom);
  // Entrées listées : '*' d'abord, puis membres nominatifs (owner exclu — implicite).
  const entrees = Object.keys(a)
    .filter(k => k === '*' || k !== ownerUid)
    .sort((x, y) => (x === '*' ? -1 : y === '*' ? 1 : 0))
    .map(cle => ({ cle, nom: cle === '*' ? t('acces.toutEspace') : nomDe(cle), niveau: a[cle], tous: cle === '*' }));

  // Candidats à l'ajout : « Tout l'espace » si absent + membres (hors owner) pas déjà listés.
  const dispo = useMemo(() => {
    const arr = [];
    if (!a['*']) arr.push({ uid: '*' });
    membres.filter(m => m.uid !== ownerUid && !a[m.uid]).forEach(m => arr.push(m));
    return arr;
  }, [a, membres, ownerUid]);

  const valider = (cle, niveau) => onChange?.(definirNiveau(a, cle, niveau));
  const retirer = (cle) => onChange?.(definirNiveau(a, cle, null));
  // Feuille GLOBALE (niveau écran) si le provider est posé — rendue dans la
  // carte, elle se coincerait dedans (calque absolu relatif au parent).
  const ouvrirGlobal = useOuvrirFormAcces();
  const ouvrir = (f) => {
    if (ouvrirGlobal) ouvrirGlobal({ ...f, titreRessource: titre, niveaux, membresDispo: dispo, onValider: valider, onRetirer: retirer });
    else setForm(f);
  };
  const ouvrirAjout = () => ouvrir({ mode: 'add', niveauInit: 'lecteur' });
  const ouvrirEdition = (e) => ouvrir({ mode: 'edit', cible: e.cle, cibleNom: e.nom, niveauInit: e.niveau });

  return (
    <View style={s.bloc}>
      <View style={s.tete}>
        {!!emoji && <Text style={s.emoji}>{emoji}</Text>}
        <View style={{ flex: 1 }}>
          <Text style={s.titre} numberOfLines={1}>{titre}</Text>
          <Text style={s.sous} numberOfLines={1}>{sousTitre || t('acces.proprietaireComplet')}</Text>
        </View>
        {/* Ajout d'accès : icône en en-tête de carte (principe bouton-icône). */}
        <TouchableOpacity hitSlop={HIT} onPress={ouvrirAjout} accessibilityLabel={t('acces.ajouterAcces')}>
          <Text style={s.plus}>＋</Text>
        </TouchableOpacity>
      </View>

      <View style={s.liste}>
        {entrees.length === 0 ? (
          <Text style={s.vide}>{t('acces.personneDautre')}</Text>
        ) : entrees.map(e => (
          <LigneSwipe key={e.cle} espacement={5} onEditer={() => ouvrirEdition(e)} onSupprimer={() => retirer(e.cle)}>
            <TouchableOpacity style={s.row} activeOpacity={0.7} onPress={() => ouvrirEdition(e)}>
              {e.tous
                ? <View style={s.avTous}><Text style={s.avTousTxt}>👥</Text></View>
                : <View style={s.avatar}><Text style={s.avatarTxt}>{(e.nom || '?').slice(0, 1).toUpperCase()}</Text></View>}
              <Text style={s.rowNom} numberOfLines={1}>{e.nom}</Text>
              <View style={[s.chip, rangNiveau(e.niveau) >= 2 ? s.chipFort : s.chipDoux]}>
                <Text style={[s.chipTxt, rangNiveau(e.niveau) >= 2 ? s.chipTxtFort : s.chipTxtDoux]}>{t(CLE_NIVEAU[e.niveau] || 'acces.niveauLecteur')}</Text>
              </View>
              <Text style={s.chev}>›</Text>
            </TouchableOpacity>
          </LigneSwipe>
        ))}

      </View>

      {/* Repli sans provider : feuille locale (peut se coincer dans la carte —
          poser FormAccesProvider au niveau de l'écran est la voie normale). */}
      {!ouvrirGlobal && (
        <FeuilleNiveauAcces
          visible={!!form} mode={form?.mode} cible={form?.cible} cibleNom={form?.cibleNom}
          titreRessource={titre} niveauInit={form?.niveauInit} niveaux={niveaux}
          membresDispo={dispo} onValider={valider} onRetirer={retirer} onFermer={() => setForm(null)} />
      )}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bloc:  { borderWidth: 1, borderColor: UI.border, borderRadius: 12, backgroundColor: UI.card, padding: 12, marginBottom: 12 },
  tete:  { flexDirection: 'row', alignItems: 'center', gap: 9 },
  emoji: { fontSize: 19 },
  titre: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  sous:  { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  liste: { borderTopWidth: 0.5, borderTopColor: UI.border, marginTop: 9, paddingTop: 5 },
  vide:  { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', paddingVertical: 6 },
  row:   { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 7 },
  avatar:  { width: 28, height: 28, borderRadius: 14, backgroundColor: UI.accentTint, alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 12, fontWeight: '800', color: UI.accent },
  avTous:  { width: 28, height: 28, borderRadius: 14, backgroundColor: UI.champ, alignItems: 'center', justifyContent: 'center' },
  avTousTxt: { fontSize: 14 },
  rowNom: { flex: 1, fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  chip:   { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 999 },
  chipFort: { backgroundColor: UI.accentTint },
  chipDoux: { backgroundColor: UI.champ },
  chipTxt: { fontSize: 11.5, fontWeight: '700' },
  chipTxtFort: { color: UI.accent },
  chipTxtDoux: { color: UI.muted },
  chev:  { fontSize: 18, color: UI.faint, width: 12, textAlign: 'center' },
  plus:  { fontSize: 20, color: UI.accent, fontWeight: '700', paddingHorizontal: 2 },
});
